const TRAIL_MAX = 1000;
const OSC_RATE = 0.022;

export function effective(param, t) {
  const mid = (param.low + param.high) / 2;
  if (!param.osc) return mid;
  const amp = (param.high - param.low) / 2;
  return mid + amp * wave(param.curve, t);
}

export function wave(curve, t) {
  const TAU = 2 * Math.PI;
  switch (curve) {
    case "sine":   return Math.sin(t);
    case "tri": {
      const u = ((t / TAU) % 1 + 1) % 1; // u in [0, 1)
      return 1 - 4 * Math.abs(u - 0.5);  // 0→-1, 0.5→+1, 1→-1
    }
    case "square": {
      const s = Math.sin(t);
      return s >= 0 ? 1 : -1;
    }
    case "saw": {
      const u = ((t / TAU) % 1 + 1) % 1;
      return 2 * u - 1; // 0→-1, 1→+1
    }
    default: return 0;
  }
}

// Each magnet orbits AROUND its axis on the unit sphere:
//   axis "x" → position varies in YZ plane: (0, cos φ, sin φ)
//   axis "y" → position varies in XZ plane: (cos φ, 0, sin φ)
//   axis "z" → position varies in XY plane: (cos φ, sin φ, 0)
export function magnetPos(m) {
  const c = Math.cos(m.phi);
  const s = Math.sin(m.phi);
  if (m.axis === "x") return { x: 0, y: c, z: s };
  if (m.axis === "y") return { x: c, y: 0, z: s };
  return                     { x: c, y: s, z: 0 };
}

export function createSim() {
  return {
    T: { x: 0.15, y: 0.05, z: 0.0, vx: 0.006, vy: 0.003, vz: 0.004 },
    magnets: {
      action: {
        axis: "x", phi: 0.0, oscPhase: 0,
        speed: { low: -0.30, high: 0.30, osc: false, curve: "sine" },
        force: { low: -120,  high: 120,  osc: false, curve: "sine" },
        effSpeed: 0, effForce: 0,
      },
      observation: {
        axis: "y", phi: 1.0, oscPhase: Math.PI * 0.6,
        speed: { low: -0.20, high: 0.20, osc: false, curve: "sine" },
        force: { low: -120,  high: 120,  osc: false, curve: "sine" },
        effSpeed: 0, effForce: 0,
      },
      relation: {
        axis: "z", phi: 2.0, oscPhase: Math.PI * 1.2,
        speed: { low: -0.40, high: 0.40, osc: false, curve: "sine" },
        force: { low: -120,  high: 120,  osc: false, curve: "sine" },
        effSpeed: 0, effForce: 0,
      },
    },
    trail: [],
    driftHist: [],
    oscClock: 0,
    paused: false,
  };
}

function force(m, T) {
  const p = magnetPos(m);
  const dx = p.x - T.x, dy = p.y - T.y, dz = p.z - T.z;
  const d2 = Math.max(dx * dx + dy * dy + dz * dz, 0.04);
  const d = Math.sqrt(d2);
  const k = (0.000018 * m.effForce) / d2;
  return { fx: (dx / d) * k, fy: (dy / d) * k, fz: (dz / d) * k };
}

export function step(sim) {
  if (sim.paused) return null;

  sim.oscClock += OSC_RATE;
  for (const m of Object.values(sim.magnets)) {
    const t = sim.oscClock + m.oscPhase;
    m.effSpeed = effective(m.speed, t);
    m.effForce = effective(m.force, t);
    m.phi += m.effSpeed * 0.012;
  }

  const f1 = force(sim.magnets.action, sim.T);
  const f2 = force(sim.magnets.observation, sim.T);
  const f3 = force(sim.magnets.relation, sim.T);
  sim.T.vx += f1.fx + f2.fx + f3.fx;
  sim.T.vy += f1.fy + f2.fy + f3.fy;
  sim.T.vz += f1.fz + f2.fz + f3.fz;

  // light damping
  sim.T.vx *= 0.998; sim.T.vy *= 0.998; sim.T.vz *= 0.998;

  const sp = Math.hypot(sim.T.vx, sim.T.vy, sim.T.vz);
  const cap = 0.045;
  if (sp > cap) {
    const s = cap / sp;
    sim.T.vx *= s; sim.T.vy *= s; sim.T.vz *= s;
  }
  sim.T.x += sim.T.vx;
  sim.T.y += sim.T.vy;
  sim.T.z += sim.T.vz;

  const r = Math.hypot(sim.T.x, sim.T.y, sim.T.z);
  const wall = 0.97;
  if (r > wall) {
    const nx = sim.T.x / r, ny = sim.T.y / r, nz = sim.T.z / r;
    const vn = sim.T.vx * nx + sim.T.vy * ny + sim.T.vz * nz;
    if (vn > 0) {
      const e = 0.65;
      sim.T.vx -= (1 + e) * vn * nx;
      sim.T.vy -= (1 + e) * vn * ny;
      sim.T.vz -= (1 + e) * vn * nz;
    }
    sim.T.x = nx * wall;
    sim.T.y = ny * wall;
    sim.T.z = nz * wall;
  }

  sim.trail.push({ x: sim.T.x, y: sim.T.y, z: sim.T.z });
  if (sim.trail.length > TRAIL_MAX) sim.trail.shift();
  sim.driftHist.push(r);
  if (sim.driftHist.length > 100) sim.driftHist.shift();

  return r;
}

export function resetTime(sim) {
  sim.T = { x: 0.15, y: 0.05, z: 0.0, vx: 0.006, vy: 0.003, vz: 0.004 };
  sim.trail.length = 0;
  sim.driftHist.length = 0;
}
